import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { timeout, catchError, switchMap, tap, retry } from 'rxjs/operators';
import { FeedMessageService, SessionService, LanguageService, showErrorAndWarning, showWarning, LoadingService, UserPinSessionService } from '@services/index.service';
import { ErrorCode, MessageType, UserAuth, QueryRequest } from '@models/index.model';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly _defaultRetry = 0;
  private readonly _defaultTimeOut = environment.timeoutDefault;

  public userPinSession: string | null = null;

  private readonly unauthenticatedEndpoints = [
    QueryRequest.authLogin,
    QueryRequest.authLoginByCertificate,
    QueryRequest.authLoginByToken,
    QueryRequest.authCheckDomain,
    QueryRequest.authPassword,
    QueryRequest.customdata,
    QueryRequest.languages,
    QueryRequest.personalSSODomain,
    QueryRequest.spConfig,
  ];

  private readonly _silentGet404Endpoints: string[] = [
    QueryRequest.signProfile,
    QueryRequest.signImage,
    QueryRequest.signLastPosition,
  ];

  constructor(
    private readonly _notify: FeedMessageService,
    private readonly _session: SessionService,
    private readonly _userPinSession: UserPinSessionService,
    private readonly _lang: LanguageService,
    private readonly _translate: TranslateService,
    private readonly _loading: LoadingService
  ) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.userPinSession = null;

    const isMicrosoft = this._isMicrosoftRequest(req.url);
    const timeoutValue = this._getNumericHeader(req.headers, 'timeout', this._defaultTimeOut);
    const retryMax = this._getNumericHeader(req.headers, 'retry', this._defaultRetry);

    if (req.headers.has('user-pin')) {
      this.userPinSession = req.headers.get('user-pin');
    }

    // Peticiones a Microsoft/Graph: las gestiona MsalInterceptor, no las tocamos.
    if (isMicrosoft) {
      return this._executeRequest(req, next, timeoutValue, retryMax);
    }

    // Endpoints públicos: no llevan Authorization.
    if (this._isUnauthEndpoint(req.url)) {
      const request = this._buildInternalRequest(req, null);
      return this._executeRequest(request, next, timeoutValue, retryMax);
    }

    const isRefreshReq = this._isRefreshTokenRequest(req.url);

    // REFRESCO PROACTIVO: si el token de la sesión de app va a caducar, lo
    // renovamos ANTES de enviar, compartiendo la ÚNICA llamada de refresh.
    // (Se salta la sesión user-pin, que se gestiona por separado.)
    const shouldRefreshAhead =
      !isRefreshReq &&
      !this.userPinSession &&
      this._session.isAccessTokenExpiring() &&
      this._session.hasRefreshToken();

    if (shouldRefreshAhead) {
      return this._session.refreshSessionShared({ source: `Interceptor.proactive:${req.url}` }).pipe(
        switchMap(() => {
          const token = this._getCurrentAccessToken();
          const request = this._buildInternalRequest(req, token);
          return this._executeRequest(request, next, timeoutValue, retryMax);
        }),
        catchError(() => {
          // Si el refresh proactivo falla, enviamos con lo que haya:
          // el manejador de 401 decidirá (coalescencia o logout).
          const token = this._getCurrentAccessToken();
          const request = this._buildInternalRequest(req, token);
          return this._executeRequest(request, next, timeoutValue, retryMax);
        })
      );
    }

    const accessToken = this._getCurrentAccessToken();
    const request = this._buildInternalRequest(req, accessToken);
    return this._executeRequest(request, next, timeoutValue, retryMax);
  }

  private _executeRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    timeoutValue: number,
    retryMax: number
  ): Observable<HttpEvent<any>> {
    let stream$ = next.handle(request);

    if (timeoutValue > 0) {
      stream$ = stream$.pipe(timeout(timeoutValue));
    }

    return stream$.pipe(
      this._retryTransient(retryMax),
      tap((ev) => {
        if (ev instanceof HttpResponse) {
          const msgType: MessageType | undefined = ev.body?.messageType;
          const msg: string | undefined = ev.body?.message;
          if (msgType) {
            this._notify.feedBackResult(msgType, msg, showWarning);
            if (msgType === 'WARNING') {
              this._loading.hideGlobalLoading();
            }
          }
        }
      }),
      catchError((error: any) => this._handleError(error, request, next))
    );
  }

  private _buildInternalRequest(req: HttpRequest<any>, token?: string | null): HttpRequest<any> {
    const INTERNAL_HEADERS = new Set(['timeout', 'retry', 'user-pin']);
    let headers = new HttpHeaders();

    for (const key of req.headers.keys()) {
      const k = (key || '').toLowerCase();
      if (INTERNAL_HEADERS.has(k)) continue;

      const values = req.headers.getAll(key);
      if (!values || values.length === 0) continue;

      for (const v of values) {
        headers = headers.append(key, v);
      }
    }

    if (!headers.has('Accept-Language')) {
      headers = headers.set('Accept-Language', this._lang.getDefaultUserLanguage());
    }

    const body = req.body;
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    const isBlob = typeof Blob !== 'undefined' && body instanceof Blob;
    const isArrayBuffer = typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer;

    if (isFormData && headers.has('Content-Type')) {
      headers = headers.delete('Content-Type');
    }

    if (!isFormData && !isBlob && !isArrayBuffer) {
      if (!headers.has('Content-Type')) {
        headers = headers.set('Content-Type', 'application/json; charset=utf-8');
      }
    }

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return req.clone({ headers });
  }

  private _getCurrentAccessToken(): string | null {
    if (this.userPinSession) {
      return this._userPinSession.getAccessToken();
    }
    return this._session.getAccessToken();
  }

  private _handleError(error: any, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (error instanceof HttpErrorResponse) {
      const isMicrosoft = this._isMicrosoftRequest(request.url);
      if (isMicrosoft) {
        this._loading.hideGlobalLoading();
        return throwError(() => error);
      }

      if (this._shouldSilenceGet404(request, error)) {
        this._loading.hideGlobalLoading();
        return throwError(() => error);
      }

      let messageType: MessageType | undefined = error.error?.messageType;
      let errorMessage: string | undefined = error.error?.message || error.error?.Message;
      let errorCode: ErrorCode | undefined = error.error?.errorCode;

      if (error.error instanceof ArrayBuffer) {
        try {
          const decoded = new TextDecoder().decode(new Uint8Array(error.error));
          const parsed = JSON.parse(decoded);
          errorMessage = parsed?.message ?? errorMessage;
          messageType = parsed?.messageType ?? messageType;
          errorCode = parsed?.errorCode ?? errorCode;
        } catch {
          this._loading.hideGlobalLoading();
        }
      }

      const contentType = error.headers?.get('content-type') || '';
      const looksHtml = contentType.includes('text/html') || this._looksLikeHtml(error.error);
      const parsingFail = (error.message ?? '').includes('Http failure during parsing');

      const status = error.status;
      const isUnauthEndpoint = this._isUnauthEndpoint(request.url);
      if ((looksHtml || parsingFail) && (status === 0 || status === 200 || status === 302)) {
        if (!isUnauthEndpoint) {
          return this._handle401UnauthorizedError(request, next);
        }
      }

      if (
        status === 401 &&
        errorCode === ErrorCode.UNAUTHORIZED &&
        !request.url.includes(QueryRequest.authRefreshToken) &&
        !isUnauthEndpoint
      ) {
        return this._handle401UnauthorizedError(request, next);
      }

      if (status === 401 && !this._session.getUserAuth()) {
        this._session.removeBrowserSession();
      }

      if (this.userPinSession && status === 401) {
        this._userPinSession.removeBrowserSession();
      }

      if (errorMessage && messageType) {
        this._notify.feedBackResult(messageType, errorMessage, showErrorAndWarning);
      } else {
        this._notify.feedBackResult(
          MessageType.ERROR,
          this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERROR.UNKNOW-ERROR')
        );
      }
    }

    this._loading.hideGlobalLoading();
    return throwError(() => error);
  }

  private _shouldSilenceGet404(request: HttpRequest<any>, error: HttpErrorResponse): boolean {
    if (request.method !== 'GET') return false;
    if (error.status !== 404) return false;
    const url = request.url || '';
    return this._silentGet404Endpoints.some((u) => url.includes(u));
  }

  private _handle401UnauthorizedError(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const usedToken = this._extractBearer(req);
    const currentToken = this._getCurrentAccessToken();

    // eslint-disable-next-line no-console
    // console.debug(`[refresh-debug] 401 reactivo url=${req.url} usedTokenTail=...${usedToken?.slice(-12)} currentTokenTail=...${currentToken?.slice(-12)}`);

    // CLAVE ANTI-CASCADA: si el token vigente YA NO es el que llevaba esta
    // petición, es que otro flujo ya refrescó mientras tanto. NO refrescamos de
    // nuevo (eso rotaría los tokens otra vez e invalidaría el recién emitido):
    // simplemente reintentamos con el token vigente.
    if (currentToken && usedToken && currentToken !== usedToken) {
      // eslint-disable-next-line no-console
      // console.debug(`[refresh-debug] ANTI-CASCADA: reintentando con token vigente, sin refrescar url=${req.url}`);
      return next.handle(this._addTokenHeader(req, currentToken));
    }

    if (!this._session.hasRefreshToken()) {
      this._session.removeBrowserSession();
      this._loading.hideGlobalLoading();
      return throwError(() => new Error(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERROR.JWT-EMPTY')));
    }

    // Refresco compartido: si ya hay uno en curso, nos "colgamos" del mismo.
    // force=true porque el token vigente ha sido rechazado por el servidor
    // (podría no estar caducado por tiempo, así que saltamos la coalescencia).
    return this._session.refreshSessionShared({ force: true, source: `Interceptor.reactive401:${req.url}` }).pipe(
      switchMap((newSession: UserAuth) => {
        if (!newSession?.accessToken) {
          this._session.removeBrowserSession();
          this._loading.hideGlobalLoading();
          return throwError(() => new Error(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERROR.JWT-EMPTY')));
        }
        return next.handle(this._addTokenHeader(req, newSession.accessToken));
      }),
      catchError((err) => {
        const refreshUnauthorized =
          err instanceof HttpErrorResponse &&
          err.status === 401 &&
          err.error?.errorCode === ErrorCode.UNAUTHORIZED;

        if (refreshUnauthorized) {
          // eslint-disable-next-line no-console
          // console.debug(`[refresh-debug] Interceptor.reactive401:${req.url} -> removeBrowserSession() (redirige a login)`);
          this._session.removeBrowserSession();
        } else {
          const mt = err?.error?.messageType;
          const msg = err?.error?.message;
          if (mt && msg) {
            this._notify.feedBackResult(mt, msg, showErrorAndWarning);
          }
        }
        this._loading.hideGlobalLoading();
        return throwError(() => err);
      })
    );
  }

  private _extractBearer(req: HttpRequest<any>): string | null {
    const h = req.headers.get('Authorization') || '';
    return h.startsWith('Bearer ') ? h.slice(7) : null;
  }

  private _addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  private _getNumericHeader(headers: HttpHeaders, name: string, fallback: number): number {
    if (!headers.has(name)) return fallback;
    const raw = headers.get(name);
    const v = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(v) && v >= 0 ? v : fallback;
  }

  private _isTransient(err: any): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    return err.status === 0 || err.status === 429 || (err.status >= 500 && err.status < 600);
  }

  private _looksLikeHtml(payload: unknown): boolean {
    if (typeof payload === 'string') {
      return /<html[\s>]|<!doctype html/i.test(payload);
    }
    if (payload instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder().decode(new Uint8Array(payload).slice(0, 200)).toLowerCase();
        return text.includes('<html') || text.includes('<!doctype html');
      } catch {
        return false;
      }
    }
    return false;
  }

  private _retryTransient<T>(maxRetries: number) {
    return (source: Observable<T>): Observable<T> => {
      if (maxRetries <= 0) return source;

      return source.pipe(
        retry({
          count: maxRetries,
          delay: (error: any, retryCount: number) => {
            if (!this._isTransient(error)) {
              this._loading.hideGlobalLoading();
              return throwError(() => error);
            }
            return timer(250 * Math.pow(2, retryCount));
          },
        })
      );
    };
  }

  private _isMicrosoftRequest(url: string): boolean {
    const u = (url || '').toLowerCase();
    if (u.startsWith('https://graph.microsoft.com/')) return true;
    if (u.includes('graph.microsoft.com')) return true;
    if (u.includes('login.microsoftonline.com')) return true;
    if (u.includes('.sharepoint.com/')) return true;
    if (u.includes('-my.sharepoint.com/')) return true;
    if (u.includes('.blob.core.windows.net/')) return true;
    if (u.includes('.azureedge.net/')) return true;
    return false;
  }

  private _isUnauthEndpoint(url: string): boolean {
    return this.unauthenticatedEndpoints.some((u) => (url || '').includes(u));
  }

  private _isRefreshTokenRequest(url: string): boolean {
    return (url || '').includes(QueryRequest.authRefreshToken);
  }
}
